import { memo } from "react";
import type { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import type { TransactionListItem } from "@common/schemas/transaction.schema";
import type { Entity } from "@common/types/entity";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import type { BulkUpdateParams } from "../../transactions/TransactionsBulkActions/lib";
import { CategoryNewValueCell } from "./CategoryNewValueCell";
import { CounterpartyNewValueCell } from "./CounterpartyNewValueCell";
import { ClassesNewValueCell } from "./ClassesNewValueCell";
import { CoaNewValueCell } from "./CoaNewValueCell";
import { EntityNewValueCell } from "./EntityNewValueCell";
import { useSimilarTransactionsNewValue } from "./SimilarTransactionsNewValueContext";

type NewValueType = BulkUpdateParams["value"];

function getStringValue(
    value: NewValueType | undefined,
    fallback: string,
): string {
    return typeof value === "string" ? value : fallback;
}

function getNullableStringValue(
    value: NewValueType | undefined,
    fallback: string | null,
): string | null {
    return value === null || typeof value === "string" ? value : fallback;
}

function getStringArrayValue(
    value: NewValueType | undefined,
    fallback: string[],
): string[] {
    return Array.isArray(value) &&
        value.every((item) => typeof item === "string")
        ? value
        : fallback;
}

function isEntityValue(value: NewValueType | undefined): value is Entity {
    return (
        typeof value === "object" &&
        value !== null &&
        !Array.isArray(value) &&
        "id" in value &&
        "name" in value
    );
}

export const NewValueCell = memo(
    ({ row }: CellContext<ViewTableFeatures, TransactionListItem, number>) => {
        const { newValuesMap, valueToAssign } =
            useSimilarTransactionsNewValue();

        const transactionId = row.original.id;
        const newValue = newValuesMap[transactionId];

        switch (valueToAssign.update) {
            case "categoryId":
                return (
                    <CategoryNewValueCell
                        transactionId={transactionId}
                        currentValue={getStringValue(
                            newValue,
                            valueToAssign.value,
                        )}
                    />
                );
            case "counterpartyId":
                return (
                    <CounterpartyNewValueCell
                        transactionId={transactionId}
                        currentValue={getNullableStringValue(
                            newValue,
                            valueToAssign.value,
                        )}
                    />
                );
            case "classes":
                return (
                    <ClassesNewValueCell
                        transactionId={transactionId}
                        classIds={getStringArrayValue(
                            newValue,
                            valueToAssign.value,
                        )}
                    />
                );
            case "coa":
                return (
                    <CoaNewValueCell
                        transactionId={transactionId}
                        transaction={row.original}
                        accountId={getNullableStringValue(
                            newValue,
                            valueToAssign.value,
                        )}
                    />
                );
            case "accrualCoa":
                return (
                    <CoaNewValueCell
                        transactionId={transactionId}
                        transaction={row.original}
                        accountId={getNullableStringValue(
                            newValue,
                            valueToAssign.value,
                        )}
                        basis={AccountingLedgerBasis.accruals}
                    />
                );
            case "entity":
                return (
                    <EntityNewValueCell
                        transaction={row.original}
                        newEntity={
                            isEntityValue(newValue)
                                ? newValue
                                : valueToAssign.value
                        }
                    />
                );
            default:
                return null;
        }
    },
);
NewValueCell.displayName = "NewValueCell";
